import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { useDebounced } from '../kits/KitEditor';
import { ConflictNotice } from '../ui/ConflictNotice';
import { trapTab } from '../ui/focus';
import { BlockEditor } from './BlockEditor';
import { ListTable } from './ListTable';
import { SectionNav } from './SectionNav';
import { BLOCKS_RESOURCE, TEMPLATES_KEY, type BlockSummary } from './types';
import { DialogLayer } from '../ui/Dialog';
import { leaveGuarded } from '../ui/unsaved';

/** Confirmation before a block or template is deleted (409 and 422 "used in …" shown in place). */
export function DeleteDialog({ what, name, url, onClose }: { what: 'blok' | 'szablon'; name: string; url: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api(url, { method: 'DELETE' }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
      onClose();
    },
  });
  const error = remove.error instanceof ApiError ? remove.error : null;
  return (
    <div className="dialog-backdrop">
      <div
        className="dialog panel"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-title"
        onKeyDown={(e) => {
          if (e.key === 'Tab') trapTab(e.currentTarget, e.nativeEvent);
        }}
      >
          <DialogLayer onClose={onClose} busy={remove.isPending} />
        <h2 id="delete-title">Usunąć {what}?</h2>
        <p>„{name}” zniknie z listy. W historii zmian zostaje wpis o usunięciu.</p>
        {error?.status === 409 && (
          <ConflictNotice
            onReload={() => {
              void queryClient.invalidateQueries({ queryKey: TEMPLATES_KEY });
              onClose();
            }}
          />
        )}
        {error && error.status !== 409 && (
          <p className="alert" role="alert">
            {error.message}
          </p>
        )}
        <div className="actions">
          <button className="btn primary" type="button" autoFocus disabled={remove.isPending} onClick={() => remove.mutate()}>
            {remove.isPending ? 'Usuwanie…' : `Usuń ${what}`}
          </button>
          <button className="btn" type="button" onClick={onClose}>
            Anuluj
          </button>
        </div>
      </div>
    </div>
  );
}

export function BlocksPage() {
  const { me } = useMe();
  const [search, setSearch] = useState('');
  // `undefined`: editor closed; `null`: adding; an id: editing that block
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [deleting, setDeleting] = useState<BlockSummary | null>(null);
  const q = useDebounced(search.trim(), 250);
  const list = useQuery({
    queryKey: [...TEMPLATES_KEY, 'blocks', q],
    placeholderData: keepPreviousData,
    queryFn: () => api<{ blocks: BlockSummary[]; truncated: boolean }>(`/api/templates/blocks${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  });
  if (!me) return null;
  const blocks = list.data?.blocks ?? [];

  return (
    <section className="page" data-feature-id="templates.blocks">
      <div className="page-head">
        <h1>Szablony</h1>
        {can(me, BLOCKS_RESOURCE, 'create') && editing === undefined && (
          <button className="btn primary" type="button" onClick={() => leaveGuarded(() => setEditing(null))}>
            Dodaj blok
          </button>
        )}
      </div>
      <SectionNav me={me} />
      <p className="muted">Blok to stała paczka sprzętu, np. „System nagłośnieniowy”, „Mikrofony”, „Konsoleta”, „Okablowanie”. Wstawisz go do sprzętówki jednym ruchem.</p>

      {editing !== undefined && <BlockEditor key={editing ?? 'new'} {...(editing !== null ? { blockId: editing } : {})} onClose={() => setEditing(undefined)} />}

      <div className="filters">
        <label className="field">
          <span>Szukaj</span>
          <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Nazwa bloku" autoComplete="off" />
        </label>
      </div>
      {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać bloków'}</p>}
      {list.data && blocks.length === 0 && <p className="muted">Nie znaleziono bloków.</p>}
      {list.data?.truncated && <p className="notice">Pokazano pierwszych 200 bloków. Zawęź wyszukiwanie, żeby zobaczyć pozostałe.</p>}
      <ListTable
        me={me}
        label="Bloki sprzętu"
        resource={BLOCKS_RESOURCE}
        rows={blocks.map((b) => ({ ...b, counts: [b.lineCount] }))}
        countLabels={['Pozycji']}
        openLabel={(b) => `Skład: ${b.name}`}
        openText={can(me, BLOCKS_RESOURCE, 'edit') ? 'Edytuj' : 'Skład'}
        onOpen={(b) => leaveGuarded(() => setEditing(b.id))}
        onDelete={(b) => setDeleting(b)}
      />
      {deleting && <DeleteDialog what="blok" name={deleting.name} url={`/api/templates/blocks/${deleting.id}?version=${deleting.version}`} onClose={() => setDeleting(null)} />}
    </section>
  );
}
